<script lang="ts" setup>
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import type { ChatMessage } from 'types';
import { computed } from 'vue';
import ChatToolCall from './ChatToolCall.vue';

const props = defineProps<{
    message: ChatMessage;
    isLast?: boolean;
    isStreaming?: boolean;
    isFetching?: boolean;
}>();

const isWaiting = computed(() =>
    props.isLast
        && props.message.role === 'assistant'
        && props.message.parts.length === 0
        && (props.isFetching || props.isStreaming),
);

const userText = computed(() => {
    if(props.message.role !== 'user') {
        return '';
    }

    const first = props.message.parts[0];

    return first?.kind === 'text' ? first.text : '';
});

function renderMarkdown(text: string): string {
    const html = marked.parse(text, { async: false, breaks: true }) as string;

    return DOMPurify.sanitize(html);
}
</script>

<template>
    <div :class="message.role === 'user' ? 'flex justify-end' : 'flex justify-start'">
        <div class="max-w-[85%]">
            <div
                v-if="message.role === 'user'"
                class="rounded-lg bg-primary px-3 py-2 text-sm whitespace-pre-wrap text-white">
                {{ userText }}
            </div>

            <template v-else>
                <template
                    v-for="(part, i) in message.parts"
                    :key="i">
                    <div
                        v-if="part.kind === 'text' && part.text"
                        class="my-1 rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-900 dark:bg-neutral-800 dark:text-neutral-200">
                        <div
                            class="chat-prose"
                            v-html="renderMarkdown(part.text)" />
                    </div>
                    <ChatToolCall
                        v-else-if="part.kind === 'tool'"
                        :tool-call="part" />
                </template>

                <div
                    v-if="isWaiting"
                    class="my-1 flex items-center gap-1 rounded-lg bg-neutral-100 px-3 py-2 dark:bg-neutral-800">
                    <span class="inline-block size-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:0ms]" />
                    <span class="inline-block size-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:150ms]" />
                    <span class="inline-block size-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:300ms]" />
                </div>
            </template>
        </div>
    </div>
</template>
